import "./ManageUser.scss";
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import AbunTable from "components/AbunTable/AbunTable";
import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { adminGetAIComparisonPages, adminGetAIComparisonPageData } from "../../utils/api"; 
import "./integration.scss";

export interface ComparisonPageData {
    id: number
    comp_id: string
    comparison_topic: string
    comparison_type: string
    created_on: string
    comparison_link?: string
    // Admin-specific fields
    user_id?: number
    user_email?: string
    website_domain?: string
    website_name?: string
    user_plan?: string
}

interface AdminComparisonResponse {
    status: string
    data: {
        pages: ComparisonPageData[]
        total_count: number
    }
}

export default function ComparisonPage(props: { 
    comparisonPages?: Array<ComparisonPageData>, 
    showDateColumn?: boolean, 
    isAdmin?: boolean,
    userId?: string,  // For filtering by specific user in admin
    searchTerm?: string  // For searching
}) {
    // ---------------------- STATE ----------------------
    const [showComparisonPages, setComparisonPages] = useState(false);
    const [adminComparisonPages, setAdminComparisonPages] = useState<ComparisonPageData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadingRowId, setLoadingRowId] = useState<string | null>(null);

    // ---------------------- HOOKS ----------------------
    const navigate = useNavigate();

    // ---------------------- NON STATE CONSTANTS ----------------------
    const pageSizes = [10, 50, 150, 250, 500];
    

    // ---------------------- ADMIN API CALLS ----------------------
    const fetchAdminComparisonPages = async (userId?: string, search?: string) => {
        setLoading(true);
        setError(null);
        
        try {
            
            // Use the API function instead of fetch
            const response = await adminGetAIComparisonPages(userId, search);
            
            if (response.status === 200 && response.data?.status === 'success') {
                setAdminComparisonPages(response.data.data.pages || []);
            } else {
                throw new Error(response.data?.message || 'Failed to fetch comparison pages');
            }
        } catch (err: any) {
            
            // Handle different error types
            let errorMessage = 'Failed to fetch comparison pages';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------- ADMIN Comparison PAGE DATA FETCH ----------------------
    const fetchAdminComparisonPageData = async (compId: string) => {
        setLoadingRowId(compId);
        
        try {
            // Use the API function instead of fetch
            const response = await adminGetAIComparisonPageData(compId);
            
            if (response.status === 200 && response.data?.status === 'success' && response.data?.comparison_data) {
                const comparisonData = response.data.comparison_data;
                
                // Navigate to the comparison page view - adjust this route as needed
                navigate(`/admin/view-comparison-page/${comparisonData.comp_id}`, {
                    state: {
                        compId: comparisonData.comp_id,
                        comparisonType: comparisonData.comparison_type,
                        comparisonTopic: comparisonData.comparison_topic,
                        comparisonData: comparisonData,
                        fromAdmin: true,
                        navigationTimestamp: Date.now()
                    },
                    replace: false
                });
            } else {
                throw new Error(response.data?.message || 'Invalid response format');
            }
        } catch (err: any) {
            console.error('Error fetching admin comparison page data:', err);
            
            // Handle different error types
            let errorMessage = 'Failed to fetch comparison page data';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoadingRowId(null);
        }
    };

    // ---------------------- EFFECTS ----------------------
    useEffect(() => {
        if (props.isAdmin && showComparisonPages) {
            fetchAdminComparisonPages(props.userId, props.searchTerm);
        }
    }, [props.isAdmin, showComparisonPages, props.userId, props.searchTerm]);

    useEffect(() => {
        if (props.isAdmin && showComparisonPages && props.userId) {
            fetchAdminComparisonPages(props.userId, props.searchTerm);
        }
    }, [props.userId]);


    // ---------------------- HELPER FUNCTIONS ----------------------
    const truncateComparisonId = (compId: string, maxLength: number = 20) => {
        return compId.length > maxLength ? `${compId.substring(0, maxLength)}...` : compId;
    };

    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            
            // Get day with ordinal suffix
            const day = date.getDate();
            const getOrdinalSuffix = (day: number): string => {
                if (day > 3 && day < 21) return 'th';
                switch (day % 10) {
                    case 1: return 'st';
                    case 2: return 'nd';
                    case 3: return 'rd';
                    default: return 'th';
                }
            };
            
            // Get month abbreviation
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[date.getMonth()];
            
            // Get year (last 2 digits)
            const year = date.getFullYear().toString().slice(-2);
            
            // Get time in 12-hour format
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            
            return `${day}${getOrdinalSuffix(day)} ${month}, ${year}, ${displayHours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString; // Return original string if formatting fails
        }
    };

    // ---------------------- HANDLERS ----------------------
    const handleViewComparisonPage = (comparisonPage: ComparisonPageData) => {
        if (!comparisonPage.comp_id) {
            console.error("Invalid comparison page data");
            return;
        }
        fetchAdminComparisonPageData(comparisonPage.comp_id.trim());
    };

    // ---------------------- TABLE COLUMN DEFS ----------------------
    const columnHelper = createColumnHelper<ComparisonPageData>();
    const columnDefs: ColumnDef<ComparisonPageData, any>[] = [
        columnHelper.accessor((row: ComparisonPageData) => row.comp_id, {
            id: 'comp_id',
            header: "Comparison ID",
            cell: props => {
                const fullId = props.row.original.comp_id;
                const truncatedId = truncateComparisonId(fullId);
                const isRowLoading = loadingRowId === fullId;
                
                return (
                    <span 
                        title={fullId} 
                        style={{ 
                            cursor: fullId.length > 20 ? 'help' : 'default',
                            opacity: isRowLoading ? 0.6 : 1
                        }}
                    >
                        {truncatedId}
                    </span>
                );
            },
            enableGlobalFilter: true,
            enableSorting: true
        }),

        columnHelper.accessor((row: ComparisonPageData) => row.created_on, {
            id: 'created_on',
            header: "Created On",
            cell: info => {
                const isRowLoading = loadingRowId === info.row.original.comp_id;
                return (
                    <span style={{ opacity: isRowLoading ? 0.6 : 1 }}>
                        {formatDate(info.getValue())}
                    </span>
                );
            },
            enableGlobalFilter: false,
        }),

        columnHelper.accessor((row: ComparisonPageData) => row.comparison_type, {
            id: 'comparison_type',
            header: "Comparison Title",
            cell: info => {
                const value = info.getValue();
                const isRowLoading = loadingRowId === info.row.original.comp_id;
                return (
                    <span style={{ opacity: isRowLoading ? 0.6 : 1 }}>
                        {value ? value.charAt(0).toUpperCase() + value.slice(1) : "---"}
                    </span>
                );
            },
            enableGlobalFilter: true,
            enableSorting: true
        }),
    ];

    // Add comp link column last
    columnDefs.push(
        columnHelper.display({
            id: 'comparison_link',
            header: "View Comparison",
            cell: props => {
                const original = props.row.original;
                const isRowLoading = loadingRowId === original.comp_id;
                
                // For admin, use button that calls our handler
                return (
                    <button 
                        className="button is-primary"
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleViewComparisonPage(original);
                        }}
                        disabled={isRowLoading}
                        style={{ 
                            backgroundColor: isRowLoading ? "#86a5ec" : "",
                            cursor: isRowLoading ? "not-allowed" : "pointer"
                        }}
                    >
                        {isRowLoading ? "Loading..." : "View"}
                    </button>
                );
            },
            enableGlobalFilter: false,
        })
    );

    // ---------------------- DATA SOURCE ----------------------
    const dataToUse = props.isAdmin ? adminComparisonPages : (props.comparisonPages || []);

    // ---------------------- MAIN RENDER CODE ----------------------
    return (
        <div className={"card w-100 mt-4"}>
            <header
                className="card-header is-clickable"
                onClick={() => setComparisonPages(!showComparisonPages)}
                >
                <p className="card-header-title">
                    Comparison Pages 
                </p>
                <span className="card-header-icon">
                  <FontAwesomeIcon icon={showComparisonPages ? faChevronDown : faChevronRight} />
                </span>
            </header>
            {showComparisonPages && (
                <div className={"card-content"}>
                    <div className={"content"}>
                        {error && (
                            <div className="notification is-danger">
                                <button className="delete" onClick={() => setError(null)}></button>
                                {error}
                            </div>
                        )}
                        
                        <div className={"all-users-container"}>
                            <AbunTable 
                                tableContentName={"Comparison Pages"}
                                tableData={dataToUse}
                                columnDefs={columnDefs}
                                pageSizes={pageSizes}
                                initialPageSize={pageSizes[0]}
                                noDataText={props.isAdmin && loading ? "Loading..." : "No Comparison Pages Found"}
                                enableSorting={true}
                                searchboxPlaceholderText={"Search Comparison pages..."} 
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}